import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import actions from '../../../../constants/ActionTypes/Navigation';
import { getMenuItemByPathname, getPathname } from 'containers/Navigation/utils';
import { ZERO } from 'constants/App';
import { MENU_ITEMS, supportObj } from 'containers/Navigation/constants';

/**
 * Custom React hook to determine and update the active menu item based on the current URL pathname.
 *
 * - Uses `useMemo` to optimize the lookup of the current menu item.
 * - Uses `useEffect` to dispatch the active menu item to Redux.
 *
 * This hook does not return a value but updates the Redux store with the active menu item.
 */
const useActiveMenuItem = (setMenu) => {
    const location = useLocation();
    const dispatch = useDispatch();

    const updatedPathname = useMemo(() => getPathname(location.pathname), [location.pathname]);

    const currentMenuItem = useMemo(() => {
        return (
            getMenuItemByPathname(updatedPathname, MENU_ITEMS) ||
            (supportObj.route.test(updatedPathname) ? supportObj : null)
        );
    }, [updatedPathname]);

    useEffect(() => {
        dispatch({ type: actions.SET_ACTIVE_MENU_ITEM, data: [] });

        if (currentMenuItem) {
            dispatch({ type: actions.SET_ACTIVE_MENU_ITEM, data: currentMenuItem?.willChange || [] });
            if (currentMenuItem.willChange.length > ZERO) {
                const parentIds = currentMenuItem.willChange.filter((id) => id !== currentMenuItem.id);

                setMenu((prevState) => {
                    const newMenuState = { ...prevState };
                    parentIds.forEach((id) => {
                        if (!newMenuState[id]) {
                            newMenuState[id] = true;
                        }
                    });

                    return newMenuState;
                });
            }
        }
    }, [currentMenuItem]);
};

export default useActiveMenuItem;
